import { useCallback, useEffect, useState } from 'react';
import type { Live } from '@liveforge/shared';
import { errorMessage } from '../lib/api';
import { getLive, listLives } from '../lib/resources';

export interface LivesResult {
  lives: Live[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** Lets a page apply a create/delete result without a round trip. */
  setLives: (lives: Live[]) => void;
}

export function useLives(showId: string | undefined): LivesResult {
  const [lives, setLives] = useState<Live[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!showId) {
      setLoading(false);
      setError('No show was requested.');
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    listLives(showId, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setLives(data);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(errorMessage(cause));
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
      });
    return () => controller.abort();
  }, [showId, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { lives, loading, error, reload, setLives };
}

export interface LiveResult {
  live: Live | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  setLive: (live: Live) => void;
}

export function useLive(liveId: string | undefined): LiveResult {
  const [live, setLive] = useState<Live | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!liveId) {
      setLoading(false);
      setError('No live was requested.');
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    getLive(liveId, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setLive(data);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(errorMessage(cause));
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
      });
    return () => controller.abort();
  }, [liveId, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { live, loading, error, reload, setLive };
}
